import {
	type CurrencyEnum,
	type OrderShippingMethod,
	OrderQuoteOfferBatchAction,
	OrderSampleChoice,
	useGetOrderQuoteOfferBatchLazyQuery,
} from '@codezo/graphql';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Batch, Offer } from './types';

interface UseBatchSelectionArgs {
	batch: Batch;
	isAccepted: boolean;
	selectedCurrency: CurrencyEnum;
}

/**
 * What an offer card is previewing, and what that combination totals.
 *
 * Extracted so the two offer cards — the current one and the v2 page's — cannot
 * drift apart on it. None of this is layout: it is the four axes a card lets an
 * admin move (quantity, shipping method, sample choice, which products are in),
 * plus the summary request those choices imply.
 *
 * ── Why selections are held as identities, never as row ids ──
 *
 * Editing a batch's pricing or its shipping destroys and recreates those rows,
 * so an id captured once at mount points at nothing the moment the parent
 * refetches: the card drew "Not priced" / "No method selected" over a batch that
 * had both, and the summary was asked to price ids that no longer existed. A
 * tier is identified by its `orderQuoteItemQuantityId` and a delivery by its
 * shipping method within that tier — both survive an edit — and the ids the rest
 * of the card needs are derived from the batch in hand, so a refetch re-resolves
 * them.
 *
 * Empty means "nothing picked yet", which is not the same as a pick: the
 * defaults below then apply, and keep applying as the batch changes.
 */
export function useBatchSelection({ batch, isAccepted, selectedCurrency }: UseBatchSelectionArgs) {
	const [pickedQuantities, setPickedQuantities] = useState<Record<string, string>>({});
	const [pickedMethods, setPickedMethods] = useState<Record<string, OrderShippingMethod>>({});
	/**
	 * Splits, remembered by id rather than by method.
	 *
	 * A split's `shippingMethod` is DERIVED from its first leg, so it is not an
	 * identity: a "20 Express + 80 Standard" split reports EXPRESS and a lookup by
	 * method lands on the plain Express row instead — the pick would appear to
	 * jump the moment it was made. And unlike a method, a split does not carry
	 * across a tier change: each tier has its own splits, so one from another tier
	 * is simply not on offer here.
	 */
	const [pickedSplits, setPickedSplits] = useState<Record<string, string>>({});

	/**
	 * The sample choice being previewed, per offer — the third axis, beside the
	 * tier and the shipping method, and previewed for the same reason: to try a
	 * combination and watch the total move.
	 *
	 * Admin never OWNS this answer. It's the client's, writable only by the accept
	 * payload and their saved selection, so nothing here is persisted.
	 *
	 * Defaults to SAMPLE_WITH_SHIPPING rather than the client's NO_SAMPLE, to
	 * agree with the argument-less `batch.summary` the first render leans on —
	 * that counts every authored sample as taken with shipping. Starting at
	 * NO_SAMPLE would have drawn tiles saying one thing over a total saying
	 * another, before anyone had clicked. It's also the right opening question for
	 * the person quoting: this is the sample you are offering, at full price.
	 */
	const [sampleChoices, setSampleChoices] = useState<Record<string, OrderSampleChoice>>({});

	/**
	 * Products taken out of the previewed order — the fourth axis, and the
	 * client's to decide like the rest. Everything starts in; deselecting one asks
	 * what the batch totals without it. Preview only, never persisted.
	 */
	const [excludedOfferIds, setExcludedOfferIds] = useState<Set<string>>(new Set());

	/** Set once the admin picks something, so the first render can lean on the
	 * argument-less `batch.summary` instead of asking for a summary it already has. */
	const touched = useRef(false);

	const { selectedPricings, selectedDeliveries } = useMemo(() => {
		const pricings: Record<string, string> = {};
		const deliveries: Record<string, string> = {};

		for (const offer of batch.offers) {
			const tiers = offer.pricing ?? [];
			if (tiers.length === 0) continue;

			// Defaults to the tier the client accepted, else the cheapest quoted.
			const quantityId =
				pickedQuantities[offer.id] ??
				tiers.find((p) => p.id === offer.acceptedPricingId)?.orderQuoteItemQuantityId;
			const tier = tiers.find((p) => p.orderQuoteItemQuantityId === quantityId) ?? tiers[0];
			pricings[offer.id] = tier.id;

			const options = tier.deliveries ?? [];

			// By id first: a split is only ever identified this way. Absent from this
			// tier — because the tier changed, or because it was deleted — it falls
			// through to the defaults rather than resolving to something else.
			const pickedSplitId = pickedSplits[offer.id];
			const bySplit = pickedSplitId ? options.find((d) => d.id === pickedSplitId) : undefined;
			if (bySplit) {
				deliveries[offer.id] = bySplit.id;
				continue;
			}

			const picked = pickedMethods[offer.id];
			// Splits are excluded: theirs is a derived method that collides with the
			// real one, and the plain row is what a picked METHOD means.
			const byMethod = picked
				? options.find((d) => !d.isSplit && d.shippingMethod === picked)
				: undefined;
			if (byMethod) {
				// The method carries across a tier change; a tier that doesn't offer it
				// falls through to the defaults below rather than showing nothing.
				deliveries[offer.id] = byMethod.id;
				continue;
			}

			// Once accepted, the client's answer is the whole truth — including a
			// dangling one, which is a record of what they took rather than a live
			// selection. A null `acceptedDeliveryId` means no per-product method
			// applied: the batch's plan shipped that product, or nothing was picked.
			// Falling back to the tier's first delivery invented a method the client
			// never chose and fed it into the summary, pricing shipping nobody agreed to.
			if (isAccepted) {
				if (offer.acceptedDeliveryId) deliveries[offer.id] = offer.acceptedDeliveryId;
				continue;
			}

			const fallback =
				options.find((d) => d.id === offer.acceptedDeliveryId) ?? options[0] ?? null;
			if (fallback) deliveries[offer.id] = fallback.id;
		}

		return { selectedPricings: pricings, selectedDeliveries: deliveries };
	}, [batch.offers, isAccepted, pickedQuantities, pickedMethods, pickedSplits]);

	/* Every product is the client's to take out. A taken plan used to pin the
	   ones it covered — its price bought them as a set, so dropping one would
	   have billed the plan's full shipping against a subset it no longer matched
	   — and that was the only thing that ever overrode this. */
	const includedOffers = batch.offers.filter((o) => !excludedOfferIds.has(o.id));

	// Only the products being priced, matching the client — `includedOfferIds`
	// decides what counts either way, but the two cards should ask the same
	// question for the same on-screen state.
	const selectedSampleChoices = includedOffers
		.filter((o) => o.sample)
		.map((o) => ({
			offerId: o.id,
			sampleChoice: sampleChoices[o.id] ?? OrderSampleChoice.SampleWithShipping,
		}));

	// The defaults applied, so the tiles highlight what the summary is pricing.
	// Passing the raw state instead left every tile unselected until the first
	// click, over a total that had already assumed one.
	const resolvedSampleChoices = Object.fromEntries(
		selectedSampleChoices.map((c) => [c.offerId, c.sampleChoice])
	);

	const [fetchBatchSummary, { data: summaryData }] = useGetOrderQuoteOfferBatchLazyQuery();
	const liveSummary = summaryData?.getOrderQuoteOfferBatch?.summary ?? batch.summary;

	/* What the previewed combination earns — amount, the product revenue it is
	 * measured over, and the percentage between them, all decided by the API.
	 *
	 * It rides the same request as the summary so the two can never describe
	 * different combinations, and falls back to the same argument-less figures on
	 * the first render. Admin-only: null for every other role, guest preview
	 * included, which is why nothing here may compute a replacement. */
	const liveMargin = summaryData?.getOrderQuoteOfferBatch?.margin ?? batch.margin;

	const fetchSummary = (
		pricings: Record<string, string>,
		deliveries: Record<string, string>,
		choices: Array<{ offerId: string; sampleChoice: OrderSampleChoice }>,
		includedOfferIds: string[]
	) => {
		const selectedPricingIds = Object.values(pricings).filter(Boolean);
		if (selectedPricingIds.length === 0) return;
		const selectedDeliveryIds = Object.values(deliveries).filter(Boolean);
		fetchBatchSummary({
			variables: {
				id: batch.id,
				inCurrency: selectedCurrency,
				selectedPricingIds,
				selectedDeliveryIds:
					selectedDeliveryIds.length > 0 ? selectedDeliveryIds : undefined,
				selectedSampleChoices: choices,
				includedOfferIds,
			},
			fetchPolicy: 'no-cache',
		});
	};

	// One place decides what the total prices: whatever the card is showing right
	// now. Every reason the answer could move is a dependency — a pick, a currency
	// switch, and a refetch that changed either the numbers or the ids the
	// selection resolves to (a delivery edit recreates delivery rows, so the ids
	// move even when the totals don't).
	//
	// An accepted batch asks on mount: the argument-less `batch.summary` knows
	// nothing of the per-product methods the client actually took, so it priced a
	// combination they never agreed to. Before any pick on a live batch that same
	// fallback is already correct, so the first render costs no request.
	const summarySignature = JSON.stringify(batch.summary);
	const selectionSignature = JSON.stringify([
		selectedPricings,
		selectedDeliveries,
		selectedSampleChoices,
		[...excludedOfferIds].sort(),
	]);

	useEffect(() => {
		if (!touched.current && !summaryData && !isAccepted) return;
		fetchSummary(
			selectedPricings,
			selectedDeliveries,
			selectedSampleChoices,
			includedOffers.map((o) => o.id)
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectionSignature, summarySignature, selectedCurrency, isAccepted]);

	// Backend-driven: inline editing is allowed only when EDIT_CONTENT is available.
	const canEditContent = (batch.availableActions ?? []).some(
		(a) => a.action === OrderQuoteOfferBatchAction.EditContent && !a.blocked
	);

	// Same action the header's menu offers, surfaced beside the total as well —
	// that's where the decision to send is actually made.
	const sendAction = (batch.availableActions ?? []).find(
		(a) => a.action === OrderQuoteOfferBatchAction.SendToClient
	);

	/* Picks record the identity behind the row that was clicked, not the row —
	 * see the note above. The summary follows on its own; nothing here fetches. */

	const selectPricing = (offerId: string, pricingId: string) => {
		const tier = batch.offers
			.find((o) => o.id === offerId)
			?.pricing?.find((p) => p.id === pricingId);
		if (!tier) return;
		touched.current = true;
		setPickedQuantities((prev) => ({ ...prev, [offerId]: tier.orderQuoteItemQuantityId }));
	};

	const selectDelivery = (offerId: string, deliveryId: string, pricingId: string) => {
		// A delivery belongs to a specific tier — picking one from a tier that
		// isn't the selected one must switch the tier too, or the selected delivery
		// would end up on a different tier than the chosen pricing.
		const tier = batch.offers
			.find((o) => o.id === offerId)
			?.pricing?.find((p) => p.id === pricingId);
		const delivery = tier?.deliveries?.find((d) => d.id === deliveryId);
		if (!tier || !delivery) return;
		touched.current = true;
		setPickedQuantities((prev) => ({ ...prev, [offerId]: tier.orderQuoteItemQuantityId }));
		/* One or the other, never both: a stale entry in the loser would win the
		   next time this offer's tier changed. */
		if (delivery.isSplit) {
			setPickedSplits((prev) => ({ ...prev, [offerId]: delivery.id }));
			setPickedMethods((prev) => {
				const { [offerId]: _dropped, ...rest } = prev;
				return rest;
			});
		} else {
			setPickedMethods((prev) => ({ ...prev, [offerId]: delivery.shippingMethod }));
			setPickedSplits((prev) => {
				const { [offerId]: _dropped, ...rest } = prev;
				return rest;
			});
		}
	};

	// Taking a product out leaves it in the offer, priced and visible — it just
	// stops counting toward the previewed total.
	const toggleOffer = (offerId: string) => {
		touched.current = true;
		setExcludedOfferIds((prev) => {
			const next = new Set(prev);
			if (next.has(offerId)) next.delete(offerId);
			else next.add(offerId);
			return next;
		});
	};

	const selectSampleChoice = (offerId: string, choice: OrderSampleChoice) => {
		touched.current = true;
		setSampleChoices((prev) => ({ ...prev, [offerId]: choice }));
	};

	return {
		selectedPricings,
		selectedDeliveries,
		excludedOfferIds,
		includedOffers: includedOffers as Offer[],
		resolvedSampleChoices,
		liveSummary,
		liveMargin,
		canEditContent,
		sendAction,
		selectPricing,
		selectDelivery,
		toggleOffer,
		selectSampleChoice,
	};
}
